import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Tooltip, Button } from '@mui/material';

// ─── Component library ───────────────────────────────────────────────────────
const COMP_ITEMS = [
  { id: 'ec2',      icon: '🖥',  label: 'EC2',          cat: 'AWS',      stroke: '#FF9900', bg: '#1c1000' },
  { id: 'lambda',   icon: '⚡',  label: 'Lambda',       cat: 'AWS',      stroke: '#FF9900', bg: '#1c1000' },
  { id: 'rds',      icon: '🗄',  label: 'RDS',          cat: 'AWS',      stroke: '#FF9900', bg: '#1c1000' },
  { id: 's3',       icon: '🪣',  label: 'S3',           cat: 'AWS',      stroke: '#FF9900', bg: '#1c1000' },
  { id: 'cf',       icon: '🌐',  label: 'CloudFront',   cat: 'AWS',      stroke: '#FF9900', bg: '#1c1000' },
  { id: 'lb',       icon: '⚖',  label: 'Load Balancer', cat: 'AWS',     stroke: '#FF9900', bg: '#1c1000' },
  { id: 'apigw',    icon: '🔀',  label: 'API Gateway',  cat: 'AWS',      stroke: '#FF9900', bg: '#1c1000' },
  { id: 'svc',      icon: '⚙',  label: 'Microservice', cat: 'Backend',  stroke: '#3b82f6', bg: '#060e1c' },
  { id: 'db',       icon: '🗃',  label: 'Database',     cat: 'Backend',  stroke: '#3b82f6', bg: '#060e1c' },
  { id: 'redis',    icon: '●',   label: 'Redis',        cat: 'Backend',  stroke: '#ef4444', bg: '#120505' },
  { id: 'kafka',    icon: '📨',  label: 'Kafka',        cat: 'Backend',  stroke: '#3b82f6', bg: '#060e1c' },
  { id: 'mq',       icon: '📦',  label: 'Message Queue',cat: 'Backend',  stroke: '#3b82f6', bg: '#060e1c' },
  { id: 'docker',   icon: '🐳',  label: 'Docker',       cat: 'Infra',    stroke: '#22d3ee', bg: '#001214' },
  { id: 'k8s',      icon: '☸',  label: 'Kubernetes',   cat: 'Infra',    stroke: '#22d3ee', bg: '#001214' },
  { id: 'cicd',     icon: '🔄',  label: 'CI/CD',        cat: 'Infra',    stroke: '#22d3ee', bg: '#001214' },
  { id: 'mon',      icon: '📊',  label: 'Monitoring',   cat: 'Infra',    stroke: '#22d3ee', bg: '#001214' },
  { id: 'web',      icon: '💻',  label: 'Web App',      cat: 'Frontend', stroke: '#a78bfa', bg: '#0e0820' },
  { id: 'mob',      icon: '📱',  label: 'Mobile App',   cat: 'Frontend', stroke: '#a78bfa', bg: '#0e0820' },
];

const TOOL_LIST = [
  { id: 'select', glyph: '↖', label: 'Select / Move' },
  { id: 'pencil', glyph: '✏', label: 'Pencil' },
  { id: 'rect',   glyph: '▭', label: 'Rectangle' },
  { id: 'circle', glyph: '○', label: 'Circle' },
  { id: 'line',   glyph: '╱', label: 'Line' },
  { id: 'arrow',  glyph: '→', label: 'Arrow' },
  { id: 'text',   glyph: 'T', label: 'Text' },
  { id: 'sticky', glyph: '📝', label: 'Sticky Note' },
];

const STROKE_COLORS = ['#ffffff','#60a5fa','#34d399','#f59e0b','#f87171','#c084fc','#94a3b8','#fb923c'];
const CATEGORIES = [...new Set(COMP_ITEMS.map(c => c.cat))];
const LS_KEY = 'exihire_arch_canvas';

let _uid = 0;
const uid = () => `s${++_uid}_${Date.now()}`;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function drawArrow(ctx, x1, y1, x2, y2, lw) {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const hs = 10 + lw * 2;
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - hs * Math.cos(angle - Math.PI / 6), y2 - hs * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - hs * Math.cos(angle + Math.PI / 6), y2 - hs * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fillStyle = ctx.strokeStyle; ctx.fill();
}

function roundRect(ctx, x, y, w, h, r) {
  const R = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + R, y);
  ctx.lineTo(x + w - R, y); ctx.quadraticCurveTo(x + w, y, x + w, y + R);
  ctx.lineTo(x + w, y + h - R); ctx.quadraticCurveTo(x + w, y + h, x + w - R, y + h);
  ctx.lineTo(x + R, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - R);
  ctx.lineTo(x, y + R); ctx.quadraticCurveTo(x, y, x + R, y);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxW, lineH) {
  const words = (text || '').split(' ');
  let line = '', cy = y;
  words.forEach(w => {
    const t = line + w + ' ';
    if (ctx.measureText(t).width > maxW && line) {
      ctx.fillText(line.trim(), x, cy); line = w + ' '; cy += lineH;
    } else { line = t; }
  });
  if (line) ctx.fillText(line.trim(), x, cy);
}

function ptToLineDist(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / l2));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

function hitShape(s, cx, cy) {
  switch (s.type) {
    case 'rect': case 'sticky': case 'component':
      return cx >= s.x && cx <= s.x + s.w && cy >= s.y && cy <= s.y + s.h;
    case 'circle':
      return cx >= s.x && cx <= s.x + s.w && cy >= s.y && cy <= s.y + s.h;
    case 'text':
      return cx >= s.x - 2 && cy >= s.y - 2 && cx <= s.x + 200 && cy <= s.y + (s.fs || 14) + 4;
    case 'line': case 'arrow':
      return ptToLineDist(cx, cy, s.x, s.y, s.x2, s.y2) < 8;
    case 'pencil':
      return s.pts?.some(([px, py], i, arr) => {
        if (!arr[i + 1]) return false;
        return ptToLineDist(cx, cy, px, py, arr[i + 1][0], arr[i + 1][1]) < 7;
      });
    default: return false;
  }
}

// Returns true if click is in the resize corner of a sticky/rect
function isResizeCorner(s, cx, cy) {
  if (s.type !== 'sticky' && s.type !== 'rect') return false;
  const rw = s.w || 150, rh = s.h || 80;
  return cx >= s.x + rw - 14 && cx <= s.x + rw + 2 &&
         cy >= s.y + rh - 14 && cy <= s.y + rh + 2;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ArchitectureCanvas({
  question,
  streamRef,
  isCameraOn,
  isMicOn,
  onSaveData,
  onNextQuestion,
  showNextButton,
  loadingNext,
}) {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const miniVideoRef = useRef(null);
  const rafRef       = useRef(null);

  // Drawing state (refs to avoid stale closures in canvas handlers)
  const isDownRef    = useRef(false);
  const startRef     = useRef({ x: 0, y: 0 });
  const liveRef      = useRef(null);
  const pencilPts    = useRef([]);
  const dragRef      = useRef(null);    // { id, ox, oy }
  const resizingRef  = useRef(null);    // { id, startX, startY, origW, origH }
  const shapesRef    = useRef([]);
  const selIdRef     = useRef(null);
  const zoomRef      = useRef(1);
  const panRef       = useRef({ x: 40, y: 40 });
  const histRef      = useRef([[]]); // array of shape snapshots
  const histIdxRef   = useRef(0);

  // React state (drives re-renders / UI)
  const [shapes, setShapesState]   = useState([]);
  const [tool, setTool]            = useState('rect');
  const [color, setColor]          = useState('#ffffff');
  const [fill, setFill]            = useState('');
  const [lw, setLw]                = useState(2);
  const [zoom, setZoom]            = useState(1);
  const [selId, setSelId]          = useState(null);
  const [textEdit, setTextEdit]    = useState(null); // { screenX, screenY, canX, canY, isSticky }
  const [textVal, setTextVal]      = useState('');
  const [saveSt, setSaveSt]        = useState('');   // '' | 'saved'
  const [activeCat, setActiveCat]  = useState('AWS');
  const [canUndo, setCanUndo]      = useState(false);
  const [canRedo, setCanRedo]      = useState(false);

  // Keep refs in sync
  const toolRef  = useRef(tool);
  const colorRef = useRef(color);
  const fillRef  = useRef(fill);
  const lwRef    = useRef(lw);
  useEffect(() => { toolRef.current  = tool;  }, [tool]);
  useEffect(() => { colorRef.current = color; }, [color]);
  useEffect(() => { fillRef.current  = fill;  }, [fill]);
  useEffect(() => { lwRef.current    = lw;    }, [lw]);
  useEffect(() => { shapesRef.current = shapes; }, [shapes]);
  useEffect(() => { selIdRef.current  = selId;  }, [selId]);
  useEffect(() => { zoomRef.current   = zoom;   }, [zoom]);

  const setShapes = useCallback((fn) => {
    setShapesState(prev => {
      const next = typeof fn === 'function' ? fn(prev) : fn;
      shapesRef.current = next;
      return next;
    });
  }, []);

  // ── History helpers ──────────────────────────────────────────────────────
  const pushHistory = useCallback((newShapes) => {
    const h = histRef.current.slice(0, histIdxRef.current + 1);
    h.push([...newShapes]);
    histRef.current = h;
    histIdxRef.current = h.length - 1;
    setCanUndo(histIdxRef.current > 0);
    setCanRedo(false);
  }, []);

  const addShape = useCallback((s) => {
    const next = [...shapesRef.current, s];
    setShapes(next);
    pushHistory(next);
  }, [setShapes, pushHistory]);

  const undo = useCallback(() => {
    if (histIdxRef.current <= 0) return;
    histIdxRef.current--;
    const snap = [...histRef.current[histIdxRef.current]];
    setShapes(snap);
    setCanUndo(histIdxRef.current > 0);
    setCanRedo(true);
  }, [setShapes]);

  const redo = useCallback(() => {
    if (histIdxRef.current >= histRef.current.length - 1) return;
    histIdxRef.current++;
    const snap = [...histRef.current[histIdxRef.current]];
    setShapes(snap);
    setCanUndo(true);
    setCanRedo(histIdxRef.current < histRef.current.length - 1);
  }, [setShapes]);

  const deleteSelected = useCallback(() => {
    const id = selIdRef.current;
    if (!id) return;
    const next = shapesRef.current.filter(s => s.id !== id);
    setShapes(next);
    pushHistory(next);
    setSelId(null);
  }, [setShapes, pushHistory]);

  const clearAll = useCallback(() => {
    setShapes([]);
    pushHistory([]);
    setSelId(null);
  }, [setShapes, pushHistory]);

  // ── Restore from localStorage on mount ──────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed.shapes) && parsed.shapes.length > 0) {
          setShapes(parsed.shapes);
          pushHistory(parsed.shapes);
        }
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Persist to localStorage on every shapes change ───────────────────────
  useEffect(() => {
    if (shapes.length === 0) return;
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ shapes, savedAt: new Date().toISOString() }));
    } catch {}
  }, [shapes]);

  // ── Canvas rendering ─────────────────────────────────────────────────────
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, W, H);

    // Dot grid
    const gs = Math.max(8, 24 * zoomRef.current);
    const ox = ((panRef.current.x % gs) + gs) % gs;
    const oy = ((panRef.current.y % gs) + gs) % gs;
    ctx.fillStyle = '#1e293b';
    for (let x = ox; x < W; x += gs)
      for (let y = oy; y < H; y += gs)
        ctx.fillRect(x - 0.5, y - 0.5, 1.5, 1.5);

    ctx.save();
    ctx.translate(panRef.current.x, panRef.current.y);
    ctx.scale(zoomRef.current, zoomRef.current);

    const all = [...shapesRef.current, liveRef.current].filter(Boolean);
    all.forEach(s => renderShape(ctx, s, selIdRef.current));

    ctx.restore();
  }, []);

  function renderShape(ctx, s, selectedId) {
    ctx.save();
    ctx.strokeStyle = s.color || '#ffffff';
    ctx.lineWidth   = s.lw || 2;
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.font = `${s.fs || 14}px Inter, sans-serif`;

    if (s.id && s.id === selectedId) {
      ctx.shadowColor = '#3b82f6'; ctx.shadowBlur = 10;
    }

    switch (s.type) {
      case 'rect': {
        if (s.fill) { ctx.fillStyle = s.fill; ctx.fillRect(s.x, s.y, s.w, s.h); }
        ctx.strokeRect(s.x, s.y, s.w, s.h);
        if (s.text) {
          ctx.fillStyle = s.color || '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.shadowBlur = 0;
          ctx.fillText(String(s.text), s.x + s.w / 2, s.y + s.h / 2);
        }
        // Resize handle when selected
        if (s.id === selectedId) {
          ctx.shadowBlur = 0;
          ctx.fillStyle = '#3b82f6';
          ctx.fillRect(s.x + s.w - 8, s.y + s.h - 8, 8, 8);
        }
        break;
      }
      case 'circle': {
        ctx.beginPath();
        ctx.ellipse(s.x + s.w / 2, s.y + s.h / 2, Math.max(1, Math.abs(s.w / 2)), Math.max(1, Math.abs(s.h / 2)), 0, 0, Math.PI * 2);
        if (s.fill) { ctx.fillStyle = s.fill; ctx.fill(); }
        ctx.stroke();
        break;
      }
      case 'line': {
        ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(s.x2, s.y2); ctx.stroke();
        break;
      }
      case 'arrow': {
        drawArrow(ctx, s.x, s.y, s.x2, s.y2, s.lw || 2);
        break;
      }
      case 'pencil': {
        if (!s.pts || s.pts.length < 2) break;
        ctx.beginPath(); ctx.moveTo(s.pts[0][0], s.pts[0][1]);
        s.pts.slice(1).forEach(([px, py]) => ctx.lineTo(px, py));
        ctx.stroke();
        break;
      }
      case 'text': {
        ctx.fillStyle = s.color || '#fff';
        ctx.textAlign = 'left'; ctx.textBaseline = 'top';
        ctx.shadowBlur = 0;
        ctx.font = `${s.fs || 14}px Inter, sans-serif`;
        ctx.fillText(String(s.text || ''), s.x, s.y);
        // Selection indicator
        if (s.id === selectedId) {
          ctx.shadowBlur = 0;
          const metrics = ctx.measureText(String(s.text || ''));
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 2]);
          ctx.strokeRect(s.x - 2, s.y - 2, metrics.width + 4, (s.fs || 14) + 4);
          ctx.setLineDash([]);
        }
        break;
      }
      case 'sticky': {
        const sw = s.w || 150, sh = s.h || 80;
        ctx.fillStyle = '#fef08a'; ctx.strokeStyle = '#ca8a04'; ctx.lineWidth = 1.5;
        ctx.shadowBlur = s.id === selectedId ? 0 : 0;
        roundRect(ctx, s.x, s.y, sw, sh, 6); ctx.fill(); ctx.stroke();
        ctx.fillStyle = '#1a1700'; ctx.font = '11px Inter, sans-serif';
        ctx.textAlign = 'left'; ctx.textBaseline = 'top'; ctx.shadowBlur = 0;
        wrapText(ctx, s.text || '', s.x + 8, s.y + 8, sw - 16, 15);
        // Resize handle at bottom-right corner when selected
        if (s.id === selectedId) {
          ctx.fillStyle = '#ca8a04';
          ctx.beginPath();
          ctx.moveTo(s.x + sw - 12, s.y + sh);
          ctx.lineTo(s.x + sw, s.y + sh);
          ctx.lineTo(s.x + sw, s.y + sh - 12);
          ctx.fill();
        }
        break;
      }
      case 'component': {
        const cw = s.w || 110, ch = s.h || 52;
        ctx.fillStyle = s.bg || '#060e1c';
        roundRect(ctx, s.x, s.y, cw, ch, 7); ctx.fill();
        ctx.strokeStyle = s.stroke || '#3b82f6'; ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.font = '16px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(s.icon || '□', s.x + 22, s.y + ch / 2);
        ctx.fillStyle = '#e2e8f0'; ctx.font = 'bold 10px Inter, sans-serif';
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.shadowBlur = 0;
        ctx.fillText(String(s.label || ''), s.x + 38, s.y + ch / 2);
        break;
      }
      default: break;
    }
    ctx.restore();
  }

  const scheduleRedraw = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(drawCanvas);
  }, [drawCanvas]);

  useEffect(() => { scheduleRedraw(); }, [shapes, selId, zoom, scheduleRedraw]);

  // ── Resize canvas ────────────────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const r = el.getBoundingClientRect();
      canvas.width  = Math.floor(r.width);
      canvas.height = Math.floor(r.height);
      scheduleRedraw();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [scheduleRedraw]);

  // ── Attach stream to mini camera ─────────────────────────────────────────
  useEffect(() => {
    const v = miniVideoRef.current;
    const s = streamRef?.current;
    if (v && s) { v.srcObject = s; v.play().catch(() => {}); }
  }, [streamRef]);

  // ── Coordinate helpers ───────────────────────────────────────────────────
  const toCanvas = useCallback((ex, ey) => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return {
      x: (ex - r.left  - panRef.current.x) / zoomRef.current,
      y: (ey - r.top   - panRef.current.y) / zoomRef.current,
    };
  }, []);

  const hitTest = useCallback((cx, cy) => {
    const arr = shapesRef.current;
    for (let i = arr.length - 1; i >= 0; i--)
      if (hitShape(arr[i], cx, cy)) return arr[i].id;
    return null;
  }, []);

  // ── Mouse handlers ───────────────────────────────────────────────────────
  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    const { x, y } = toCanvas(e.clientX, e.clientY);
    startRef.current = { x, y };
    pencilPts.current = [[x, y]];

    const t = toolRef.current;

    if (t === 'select') {
      isDownRef.current = true;
      const hit = hitTest(x, y);

      // Check resize corner first (selected shape only)
      if (selIdRef.current && hit === selIdRef.current) {
        const selShape = shapesRef.current.find(s => s.id === selIdRef.current);
        if (selShape && isResizeCorner(selShape, x, y)) {
          resizingRef.current = {
            id: selShape.id,
            startX: x, startY: y,
            origW: selShape.w || 150,
            origH: selShape.h || 80,
          };
          return;
        }
      }

      selIdRef.current = hit;
      setSelId(hit);
      if (hit) {
        const s = shapesRef.current.find(sh => sh.id === hit);
        dragRef.current = { id: hit, ox: x - (s.x ?? 0), oy: y - (s.y ?? 0) };
      }

    } else if (t === 'text' || t === 'sticky') {
      // Do NOT set isDownRef for text/sticky — prevents onMouseUp from interfering
      // Position textarea relative to the container element (not canvas element)
      const r = containerRef.current.getBoundingClientRect();
      setTextEdit({
        screenX: e.clientX - r.left,
        screenY: e.clientY - r.top,
        canX: x,
        canY: y,
        isSticky: t === 'sticky',
      });
      setTextVal('');
      return; // skip scheduleRedraw and isDownRef
    } else if (t === 'pencil') {
      isDownRef.current = true;
      liveRef.current = { type: 'pencil', pts: [[x, y]], color: colorRef.current, lw: lwRef.current };
    } else if (t === 'rect') {
      isDownRef.current = true;
      liveRef.current = { type: 'rect', x, y, w: 0, h: 0, color: colorRef.current, fill: fillRef.current, lw: lwRef.current };
    } else if (t === 'circle') {
      isDownRef.current = true;
      liveRef.current = { type: 'circle', x, y, w: 0, h: 0, color: colorRef.current, fill: fillRef.current, lw: lwRef.current };
    } else if (t === 'line') {
      isDownRef.current = true;
      liveRef.current = { type: 'line', x, y, x2: x, y2: y, color: colorRef.current, lw: lwRef.current };
    } else if (t === 'arrow') {
      isDownRef.current = true;
      liveRef.current = { type: 'arrow', x, y, x2: x, y2: y, color: colorRef.current, lw: lwRef.current };
    }
    scheduleRedraw();
  }, [toCanvas, hitTest, scheduleRedraw]);

  const onMouseMove = useCallback((e) => {
    if (!isDownRef.current) return;
    const { x, y } = toCanvas(e.clientX, e.clientY);
    const sx = startRef.current.x, sy = startRef.current.y;

    // Resize mode
    if (resizingRef.current) {
      const { id, startX, startY, origW, origH } = resizingRef.current;
      const newW = Math.max(60, origW + (x - startX));
      const newH = Math.max(40, origH + (y - startY));
      setShapes(prev => prev.map(s => s.id === id ? { ...s, w: newW, h: newH } : s));
      scheduleRedraw();
      return;
    }

    if (toolRef.current === 'select' && dragRef.current) {
      const { id, ox, oy } = dragRef.current;
      setShapes(prev => prev.map(s => {
        if (s.id !== id) return s;
        if (s.type === 'line' || s.type === 'arrow') {
          const dx = x - ox - s.x, dy = y - oy - s.y;
          return { ...s, x: s.x + dx, y: s.y + dy, x2: s.x2 + dx, y2: s.y2 + dy };
        }
        if (s.type === 'pencil') {
          const dx = x - ox - (s.pts[0]?.[0] || 0), dy = y - oy - (s.pts[0]?.[1] || 0);
          return { ...s, pts: s.pts.map(([px, py]) => [px + dx, py + dy]) };
        }
        return { ...s, x: x - ox, y: y - oy };
      }));
    } else if (toolRef.current === 'pencil' && liveRef.current) {
      pencilPts.current.push([x, y]);
      liveRef.current = { ...liveRef.current, pts: [...pencilPts.current] };
    } else if (liveRef.current) {
      const live = liveRef.current;
      if (live.type === 'rect' || live.type === 'circle') {
        liveRef.current = { ...live, x: Math.min(sx, x), y: Math.min(sy, y), w: Math.abs(x - sx), h: Math.abs(y - sy) };
      } else if (live.type === 'line' || live.type === 'arrow') {
        liveRef.current = { ...live, x2: x, y2: y };
      }
    }
    scheduleRedraw();
  }, [toCanvas, setShapes, scheduleRedraw]);

  const onMouseUp = useCallback(() => {
    if (!isDownRef.current && !resizingRef.current) return;

    // Commit resize
    if (resizingRef.current) {
      pushHistory([...shapesRef.current]);
      resizingRef.current = null;
      scheduleRedraw();
      return;
    }

    isDownRef.current = false;

    if (dragRef.current) {
      dragRef.current = null;
      pushHistory([...shapesRef.current]);
    } else if (liveRef.current) {
      const s = liveRef.current;
      liveRef.current = null;
      const valid = s.type === 'pencil'
        ? (s.pts?.length > 3)
        : s.type === 'line' || s.type === 'arrow'
          ? (Math.hypot((s.x2 - s.x), (s.y2 - s.y)) > 4)
          : (Math.abs(s.w || 0) > 4 && Math.abs(s.h || 0) > 4);
      if (valid) addShape({ ...s, id: uid() });
    }
    scheduleRedraw();
  }, [addShape, pushHistory, scheduleRedraw]);

  // ── Text commit ──────────────────────────────────────────────────────────
  const commitText = useCallback(() => {
    if (!textEdit) return;
    const trimmed = textVal.trim();
    if (trimmed) {
      addShape({
        id: uid(),
        type: textEdit.isSticky ? 'sticky' : 'text',
        x: textEdit.canX,
        y: textEdit.canY,
        w: textEdit.isSticky ? 160 : 200,
        h: textEdit.isSticky ? 90  : 20,
        text: trimmed,
        color: textEdit.isSticky ? '#1a1700' : (colorRef.current || '#ffffff'),
        lw: 1,
        fs: textEdit.isSticky ? 12 : 15,
      });
    }
    setTextEdit(null);
    setTextVal('');
  }, [textEdit, textVal, addShape]);

  // ── Wheel zoom ───────────────────────────────────────────────────────────
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const d = e.deltaY < 0 ? 1 : -1;
    const nz = Math.min(Math.max(zoomRef.current + d * 0.1, 0.25), 4);
    zoomRef.current = nz;
    setZoom(nz);
    scheduleRedraw();
  }, [scheduleRedraw]);

  // ── Zoom buttons ─────────────────────────────────────────────────────────
  const handleZoom = useCallback((d) => {
    const nz = Math.min(Math.max(zoomRef.current + d * 0.2, 0.25), 4);
    zoomRef.current = nz; setZoom(nz); scheduleRedraw();
  }, [scheduleRedraw]);

  const fitScreen = useCallback(() => {
    zoomRef.current = 1; panRef.current = { x: 40, y: 40 };
    setZoom(1); scheduleRedraw();
  }, [scheduleRedraw]);

  // ── Drag-drop from component panel ──────────────────────────────────────
  const onDragOver = (e) => e.preventDefault();
  const onDrop = useCallback((e) => {
    e.preventDefault();
    const cid = e.dataTransfer.getData('compId');
    const comp = COMP_ITEMS.find(c => c.id === cid);
    if (!comp) return;
    const { x, y } = toCanvas(e.clientX, e.clientY);
    addShape({ id: uid(), type: 'component', x: x - 55, y: y - 26, w: 110, h: 52, icon: comp.icon, label: comp.label, stroke: comp.stroke, bg: comp.bg, color: comp.stroke, lw: 1.5 });
  }, [toCanvas, addShape]);

  // ── Export ───────────────────────────────────────────────────────────────
  const getDataURL = () => canvasRef.current?.toDataURL('image/png') || '';

  const exportPNG = () => {
    const a = document.createElement('a');
    a.download = 'architecture-design.png';
    a.href = getDataURL();
    a.click();
  };

  // ── Manual save (does NOT clear canvas) ──────────────────────────────────
  const handleManualSave = useCallback(() => {
    const canvasImage = getDataURL();
    const data = {
      shapes: shapesRef.current,
      canvasImage,
      question: question?.text,
      savedAt: new Date().toISOString(),
    };
    onSaveData?.(data);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ shapes: shapesRef.current, savedAt: data.savedAt }));
    } catch {}
    setSaveSt('saved');
    setTimeout(() => setSaveSt(''), 2500);
  }, [onSaveData, question]);

  // ── Autosave every 10 seconds ─────────────────────────────────────────────
  useEffect(() => {
    const iv = setInterval(() => {
      const canvasImage = getDataURL();
      onSaveData?.({ shapes: shapesRef.current, canvasImage, question: question?.text, savedAt: new Date().toISOString() });
      setSaveSt('saved');
      setTimeout(() => setSaveSt(''), 2500);
    }, 10000);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]);

  // ── Keyboard shortcuts ───────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT','TEXTAREA'].includes(e.target.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selIdRef.current) { e.preventDefault(); deleteSelected(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, deleteSelected]);

  // ── Cursor ───────────────────────────────────────────────────────────────
  const cursor = tool === 'select' ? 'default' : (tool === 'text' || tool === 'sticky') ? 'text' : 'crosshair';

  // ── Submit handler — saves snapshot and advances ──────────────────────────
  const handleSubmit = () => {
    const canvasImage = getDataURL();
    onSaveData?.({
      shapes: shapesRef.current,
      canvasImage,
      question: question?.text,
      savedAt: new Date().toISOString(),
    });
    try { localStorage.removeItem(LS_KEY); } catch {}
    onNextQuestion?.();
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: '#0d1117', overflow: 'hidden', userSelect: 'none', color: '#e2e8f0' }}>

      {/* ═══ MAIN AREA ══════════════════════════════════════════════════════ */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, p: 1.5, gap: 1.5 }}>

        {/* LEFT: Camera + Question ─────────────────────────────────────── */}
        <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Mini camera */}
          <Box sx={{ flexShrink: 0, position: 'relative', width: '100%', aspectRatio: '4/3', bgcolor: '#1e293b', borderRadius: '12px', overflow: 'hidden', border: '1px solid #334155', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <video ref={miniVideoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            {!isCameraOn && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0f172a' }}>
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>Camera Off</Typography>
              </Box>
            )}
            <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: '4px', bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', px: '8px', py: '3px', borderRadius: '6px', fontSize: 10, fontWeight: 700 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', animation: 'pulse 1.2s infinite' }} />
              REC
            </Box>
            <Box sx={{ position: 'absolute', bottom: 8, left: 8, display: 'flex', gap: 1 }}>
              <Box sx={{ fontSize: 12, bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: '6px', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>📹</Box>
              <Box sx={{ fontSize: 12, bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: '6px', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>🎤</Box>
            </Box>
          </Box>

          {/* Question panel */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, bgcolor: '#0f172a', borderRadius: '12px', border: '1px solid #1e293b', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b' }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '1px' }}>Architecture Design</Typography>
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#f1f5f9', lineHeight: 1.6, mb: 3 }}>
              {question?.text || 'Design a scalable, highly available AWS architecture for an e-commerce platform handling 1M daily users.'}
            </Typography>

            <Box sx={{ mt: 'auto', p: 2, bgcolor: 'rgba(30, 41, 59, 0.4)', borderRadius: '10px', border: '1px solid rgba(51, 65, 85, 0.5)' }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Guidelines</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  'Drag components from the right panel onto the canvas',
                  'Connect components using arrows',
                  'Use Text tool (T) to label components',
                  'Use Sticky Note tool to add notes',
                  'Select tool to move or resize elements',
                ].map((g, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ color: '#3b82f6', fontSize: 10, mt: '2px' }}>•</Box>
                    <Typography sx={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.4 }}>{g}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* DRAWING TOOLS (Vertical Sidebar) ──────────────────────────────── */}
        <Box sx={{ width: 52, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 1, bgcolor: '#0f172a', borderRadius: '12px', border: '1px solid #1e293b' }}>
          {TOOL_LIST.map(t => (
            <Tooltip key={t.id} title={t.label} placement="right" arrow>
              <Box onClick={() => setTool(t.id)} sx={vToolBtn(tool === t.id)}>{vToolIcon(t.id)}</Box>
            </Tooltip>
          ))}
          <Box sx={{ width: '60%', height: '1px', bgcolor: '#1e293b', my: 0.5 }} />
          <Tooltip title="Undo"><Box onClick={undo} sx={vToolBtn(false, !canUndo)}>↩</Box></Tooltip>
          <Tooltip title="Redo"><Box onClick={redo} sx={vToolBtn(false, !canRedo)}>↪</Box></Tooltip>
        </Box>

        {/* CENTER: Canvas Area ───────────────────────────────────────────── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
          {/* Canvas Container — overflow visible when text input is active */}
          <Box
            ref={containerRef}
            sx={{
              flex: 1,
              position: 'relative',
              bgcolor: '#0d1117',
              borderRadius: '12px',
              border: '1px solid #1e293b',
              overflow: textEdit ? 'visible' : 'hidden',
              cursor,
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onWheel={onWheel}
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />

            {/* Canvas Toolbar (Floating) */}
            <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'center', gap: 1, p: 0.5, bgcolor: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(8px)', borderRadius: '8px', border: '1px solid rgba(51, 65, 85, 0.5)', zIndex: 10 }}>
              <Tooltip title="Zoom Out"><Box onClick={() => handleZoom(-1)} sx={canvasToolBtn()}>➖</Box></Tooltip>
              <Box sx={{ fontSize: 11, fontWeight: 600, color: '#e2e8f0', minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</Box>
              <Tooltip title="Zoom In"><Box onClick={() => handleZoom(1)} sx={canvasToolBtn()}>➕</Box></Tooltip>
              <Box sx={{ width: '1px', height: 16, bgcolor: '#334155', mx: 0.5 }} />
              <Tooltip title="Fit to Screen"><Box onClick={fitScreen} sx={canvasToolBtn()}>⛶</Box></Tooltip>
            </Box>

            {/* Save button + autosave indicator */}
            <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 10 }}>
              <Button
                onClick={handleManualSave}
                startIcon={<Box sx={{ fontSize: 14 }}>💾</Box>}
                sx={{
                  bgcolor: saveSt === 'saved' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(15, 23, 42, 0.8)',
                  color: saveSt === 'saved' ? '#22c55e' : '#94a3b8',
                  textTransform: 'none',
                  fontSize: 12,
                  borderRadius: '8px',
                  border: saveSt === 'saved' ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid rgba(51, 65, 85, 0.5)',
                  '&:hover': { bgcolor: '#1e293b', color: '#e2e8f0' },
                }}
              >
                {saveSt === 'saved' ? '✓ Saved' : 'Save'}
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: '20px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#22c55e' }}>Auto-saved</Typography>
              </Box>
            </Box>

            {/* Floating text / sticky input — positioned relative to container */}
            {textEdit && (
              <textarea
                autoFocus
                value={textVal}
                rows={textEdit.isSticky ? 3 : 1}
                onChange={e => setTextVal(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitText(); }
                  if (e.key === 'Escape') { setTextEdit(null); setTextVal(''); }
                }}
                onBlur={commitText}
                placeholder={textEdit.isSticky ? 'Add a note...' : 'Type label and press Enter'}
                style={{
                  position: 'absolute',
                  left: Math.max(4, textEdit.screenX),
                  top: Math.max(4, textEdit.screenY),
                  background: textEdit.isSticky ? '#fef08a' : 'rgba(15,23,42,0.97)',
                  color: textEdit.isSticky ? '#1a1700' : (color || '#ffffff'),
                  border: '2px solid #3b82f6',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 13,
                  fontFamily: 'Inter, sans-serif',
                  outline: 'none',
                  minWidth: 180,
                  resize: 'none',
                  zIndex: 100,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.4)',
                }}
              />
            )}
          </Box>

          {/* Footer Bar */}
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#22c55e' }}>Auto Saved</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: '#475569' }}>|</Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>{shapes.length} elements on canvas</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={exportPNG}
                sx={{ py: 1, px: 2.5, textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#94a3b8', borderColor: '#334155', borderRadius: '8px', '&:hover': { borderColor: '#475569', color: '#f8fafc', bgcolor: 'transparent' } }}
              >
                Export PNG
              </Button>
              {showNextButton && (
                <Button
                  variant="contained"
                  disabled={loadingNext}
                  onClick={handleSubmit}
                  sx={{ py: 1, px: 3, textTransform: 'none', fontSize: 13, fontWeight: 700, borderRadius: '8px', background: 'linear-gradient(135deg, #4F46E5, #3B82F6)', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)', '&:hover': { background: 'linear-gradient(135deg, #4338CA, #2563EB)', boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.4)' } }}
                >
                  Submit Design & Continue →
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* RIGHT: Component library ────────────────────────────────────── */}
        <Box sx={{ width: 260, flexShrink: 0, bgcolor: '#0f172a', borderRadius: '12px', border: '1px solid #1e293b', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #1e293b' }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', mb: 1.5 }}>Components</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {CATEGORIES.map(cat => (
                <Box key={cat} onClick={() => setActiveCat(cat)} sx={{ px: 1.5, py: 0.5, borderRadius: '6px', cursor: 'pointer', fontSize: 11, fontWeight: 600, bgcolor: activeCat === cat ? '#2563eb' : 'transparent', color: activeCat === cat ? '#fff' : '#64748b', border: activeCat === cat ? '1px solid #3b82f6' : '1px solid transparent', '&:hover': { color: activeCat === cat ? '#fff' : '#94a3b8' }, transition: 'all 0.2s' }}>
                  {cat}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#1e293b', borderRadius: '10px' } }}>
            {COMP_ITEMS.filter(c => c.cat === activeCat).map(comp => (
              <Box
                key={comp.id}
                draggable
                onDragStart={e => { e.dataTransfer.setData('compId', comp.id); e.dataTransfer.effectAllowed = 'copy'; }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  p: 1.5, borderRadius: '10px',
                  bgcolor: 'rgba(30, 41, 59, 0.4)', border: `1px solid ${comp.stroke}40`,
                  cursor: 'grab', transition: 'all 0.2s',
                  '&:hover': { border: `1px solid ${comp.stroke}`, bgcolor: 'rgba(30, 41, 59, 0.6)', transform: 'translateY(-1px)' },
                  '&:active': { cursor: 'grabbing' },
                }}
              >
                <Box sx={{ fontSize: 18, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(15, 23, 42, 0.5)', borderRadius: '8px', border: `1px solid ${comp.stroke}20` }}>{comp.icon}</Box>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#e2e8f0', flex: 1 }}>{comp.label}</Typography>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: comp.stroke }} />
              </Box>
            ))}
            <Typography sx={{ fontSize: 11, color: '#475569', textAlign: 'center', mt: 2 }}>
              Drag onto canvas
            </Typography>
          </Box>
        </Box>
      </Box>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>
    </Box>
  );
}

// ─── Styles & Helpers ──────────────────────────────────────────────────────
function vToolBtn(active = false, dimmed = false) {
  return {
    width: 36, height: 36,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '8px', cursor: dimmed ? 'default' : 'pointer',
    bgcolor: active ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
    color: dimmed ? '#1e293b' : active ? '#3b82f6' : '#64748b',
    border: active ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid transparent',
    '&:hover': dimmed ? {} : { bgcolor: active ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 41, 59, 0.5)', color: active ? '#3b82f6' : '#e2e8f0' },
    transition: 'all 0.2s',
    fontSize: 18,
  };
}

function vToolIcon(id) {
  const icons = {
    select: '🖱️', pencil: '✏️', rect: '⬜', circle: '⚪',
    line: '➖', arrow: '➡️', text: 'T', sticky: '🗒️'
  };
  return icons[id] || '•';
}

function canvasToolBtn() {
  return {
    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', borderRadius: '6px', fontSize: 12, color: '#94a3b8',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#f8fafc' },
    transition: 'all 0.2s',
  };
}
